import {
  useKineticMap,
  useStableResultData,
  MapContext,
  useMapBoxSearchContext,
  MapSearchResultItem,
  DEFAULT_ZOOM,
  useMapSearchBoxPin,
  MapSearchResultProps,
  useAppSearchHistory,
  publicUrl,
  AppSearchItemType,
  AppSearchIconKey,
  useGetNodes,
} from '@kinetic-ui/shared';
import { useSignsListQuery } from '@kinetic-ui/data-layer';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/consts';
import { useTranslation } from 'react-i18next';
import { StateLayersConfig } from '../layers';
import signIcon from './signIcon.svg';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const { searchText } = useMapBoxSearchContext();
  const { t } = useTranslation('common');
  const map = useKineticMap();

  const result = useSignsListQuery({
    fetchPolicy: 'cache-and-network',
    variables: { searchText, limit: 5 },
  });
  const resultData = useStableResultData(result);
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();

  const signs = useGetNodes(resultData?.signs);

  const { push } = useHistory();
  const { addSearchHistory } = useAppSearchHistory();

  const onClick = useCallback(
    (id: string, coordinates?: [number, number], openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const sign = signs.find((e) => e.id === id);
        if (sign?.name) {
          addSearchHistory({
            name: sign.name,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.Sign,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin({ type: 'Point', coordinates: coordinates ?? [0, 0] });
        return;
      }
      const layersToAdd: string[] = [];
      Object.entries(StateLayersConfig).forEach(([state]) => {
        !selectedLayers.includes(state) && layersToAdd.push(state);
      });

      layersToAdd.length > 0 && setSelectedLayers([...selectedLayers, ...layersToAdd]);
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      map.setViewState({
        latitude: coordinates?.[1] ?? 0,
        longitude: coordinates?.[0] ?? 0,
        zoom: DEFAULT_ZOOM,
      });
    },
    [
      addMapPin,
      addSearchHistory,
      hasEntityDrawer,
      isAppSearch,
      map,
      onResultClick,
      push,
      selectedLayers,
      setSelectedLayers,
      signs,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {signs.map((sign) => (
        <MapSearchResultItem
          key={`searchItem_${sign.id}`}
          name={sign.name}
          onClick={(openInNewWindow) =>
            onClick(sign.id, sign.location?.coordinates, openInNewWindow)
          }
          icon={<img src={signIcon} alt={t('SIGNS')} />}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
